import { Hono } from "hono";
import { z } from "zod";
import {
  oauthConformanceProfileSchema,
  type MCPAppsConformanceConfig,
} from "@mcpjam/sdk";
import {
  handleRoute,
  projectServerSchema,
} from "./auth.js";
import {
  ErrorCode,
  WebRouteError,
  assertBearerToken,
  readJsonBody,
  parseWithSchema,
} from "./errors.js";
import {
  OAuthConformanceSessionFailedError,
  OAuthConformanceSessionNotFoundError,
  UnsupportedTransportError,
  completeOAuthConformance,
  runAppsConformance,
  runProtocolConformance,
  startOAuthConformance,
  submitOAuthConformanceCode,
} from "../shared/conformance";
import { authorizeServer, toHttpConfig } from "./auth.js";
import { WEB_CALL_TIMEOUT_MS } from "../../config.js";

const conformanceWeb = new Hono();

// ── Helpers ─────────────────────────────────────────────────────────────

/** Resolve HTTP server URL and headers for conformance from authorized config. */
async function resolveHostedHttpConfig(
  c: any,
  bearerToken: string,
  body: Record<string, unknown>
): Promise<{
  serverUrl: string;
  accessToken?: string;
  customHeaders?: Record<string, string>;
}> {
  const wsBody = parseWithSchema(projectServerSchema, body);
  const auth = await authorizeServer(
    c,
    bearerToken,
    wsBody.projectId,
    wsBody.serverId,
    {
      accessScope: wsBody.accessScope,
      chatboxId: wsBody.chatboxId,
      accessVersion: wsBody.accessVersion,
    }
  );

  if (auth.serverConfig.transportType !== "http") {
    throw new WebRouteError(
      400,
      ErrorCode.FEATURE_NOT_SUPPORTED,
      "Protocol conformance requires HTTP transport"
    );
  }

  if (!auth.serverConfig.url) {
    throw new WebRouteError(
      500,
      ErrorCode.INTERNAL_ERROR,
      "Authorized server is missing URL"
    );
  }

  const oauthToken =
    typeof wsBody.oauthAccessToken === "string"
      ? wsBody.oauthAccessToken
      : undefined;
  const headers: Record<string, string> = {
    ...(auth.serverConfig.headers ?? {}),
  };
  if (oauthToken) {
    headers["Authorization"] = `Bearer ${oauthToken}`;
  }

  return {
    serverUrl: auth.serverConfig.url,
    accessToken: undefined, // OAuth token goes in headers
    customHeaders: Object.keys(headers).length > 0 ? headers : undefined,
  };
}

/** Resolve any-transport server config for Apps conformance on hosted. */
async function resolveHostedServerConfig(
  c: any,
  bearerToken: string,
  body: Record<string, unknown>
): Promise<MCPAppsConformanceConfig> {
  const wsBody = parseWithSchema(projectServerSchema, body);
  const auth = await authorizeServer(
    c,
    bearerToken,
    wsBody.projectId,
    wsBody.serverId,
    {
      accessScope: wsBody.accessScope,
      chatboxId: wsBody.chatboxId,
      accessVersion: wsBody.accessVersion,
    }
  );

  const httpConfig = toHttpConfig(
    auth,
    WEB_CALL_TIMEOUT_MS,
    typeof wsBody.oauthAccessToken === "string"
      ? wsBody.oauthAccessToken
      : undefined,
    wsBody.clientCapabilities as Record<string, unknown> | undefined
  );

  return httpConfig as MCPAppsConformanceConfig;
}

function toWebError(error: unknown): WebRouteError {
  if (error instanceof WebRouteError) return error;
  if (error instanceof UnsupportedTransportError) {
    return new WebRouteError(
      400,
      ErrorCode.FEATURE_NOT_SUPPORTED,
      error.message
    );
  }
  if (error instanceof OAuthConformanceSessionNotFoundError) {
    return new WebRouteError(404, ErrorCode.NOT_FOUND, error.message);
  }
  if (error instanceof OAuthConformanceSessionFailedError) {
    return new WebRouteError(500, ErrorCode.INTERNAL_ERROR, error.message);
  }
  return new WebRouteError(
    500,
    ErrorCode.INTERNAL_ERROR,
    error instanceof Error ? error.message : "Unknown error"
  );
}

// ── POST /protocol ──────────────────────────────────────────────────────

conformanceWeb.post("/protocol", async (c) =>
  handleRoute(c, async () => {
    const bearerToken = assertBearerToken(c);
    const body = await readJsonBody<Record<string, unknown>>(c);
    const resolved = await resolveHostedHttpConfig(c, bearerToken, body);

    try {
      const { result } = await runProtocolConformance(resolved);
      return { success: true, result };
    } catch (error) {
      throw toWebError(error);
    }
  })
);

// ── POST /apps ──────────────────────────────────────────────────────────

conformanceWeb.post("/apps", async (c) =>
  handleRoute(c, async () => {
    const bearerToken = assertBearerToken(c);
    const body = await readJsonBody<Record<string, unknown>>(c);
    const config = await resolveHostedServerConfig(c, bearerToken, body);

    try {
      const { result } = await runAppsConformance(config);
      return { success: true, result };
    } catch (error) {
      throw toWebError(error);
    }
  })
);

// ── POST /oauth/start ───────────────────────────────────────────────────

const oauthStartSchema = z
  .object({
    oauthProfile: oauthConformanceProfileSchema.optional(),
    runNegativeChecks: z.boolean().optional(),
    callbackOrigin: z.string().optional(),
  })
  .passthrough(); // project/guest fields pass through to resolveHostedHttpConfig

conformanceWeb.post("/oauth/start", async (c) =>
  handleRoute(c, async () => {
    const bearerToken = assertBearerToken(c);
    const body = await readJsonBody<Record<string, unknown>>(c);
    const resolved = await resolveHostedHttpConfig(c, bearerToken, body);
    const parsed = parseWithSchema(oauthStartSchema, body);

    if (!parsed.callbackOrigin) {
      throw new WebRouteError(
        400,
        ErrorCode.VALIDATION_ERROR,
        "callbackOrigin is required to run OAuth conformance"
      );
    }

    try {
      return await startOAuthConformance({
        defaultServerUrl: resolved.serverUrl,
        defaultCustomHeaders: resolved.customHeaders,
        redirectUrl: `${parsed.callbackOrigin.replace(
          /\/$/,
          ""
        )}/oauth/callback/debug`,
        oauthProfile: parsed.oauthProfile,
        runNegativeChecks: parsed.runNegativeChecks,
      });
    } catch (error) {
      throw toWebError(error);
    }
  })
);

// ── POST /oauth/authorize ───────────────────────────────────────────────

const oauthAuthorizeSchema = z.object({
  sessionId: z.string().min(1),
  code: z.string().min(1),
  state: z.string().optional(),
});

conformanceWeb.post("/oauth/authorize", async (c) =>
  handleRoute(c, async () => {
    const body = await readJsonBody<Record<string, unknown>>(c);
    const parsed = parseWithSchema(oauthAuthorizeSchema, body);

    const delivered = submitOAuthConformanceCode(parsed);
    if (!delivered) {
      throw new WebRouteError(
        404,
        ErrorCode.NOT_FOUND,
        "Session not found or not waiting for authorization"
      );
    }
    return { success: true };
  })
);

// ── POST /oauth/complete ────────────────────────────────────────────────

const oauthCompleteSchema = z.object({
  sessionId: z.string().min(1),
});

conformanceWeb.post("/oauth/complete", async (c) =>
  handleRoute(c, async () => {
    const body = await readJsonBody<Record<string, unknown>>(c);
    const parsed = parseWithSchema(oauthCompleteSchema, body);
    try {
      return await completeOAuthConformance(parsed);
    } catch (error) {
      throw toWebError(error);
    }
  })
);

export default conformanceWeb;
